// KDS (tela da cozinha) — kanban no computador, abas no celular.
//
// DESKTOP: "Em Producao" e "Prontos" lado a lado, cada coluna com a propria
// rolagem e os cards em grade. CELULAR: uma aba por coluna ocupando a tela
// inteira, um card por linha. Antes as duas colunas vinham EMPILHADAS nos dois
// canais: no celular cada uma ficava com ~320px, o card nao cabia e o botao de
// avancar sumia, enquanto "Prontos" vazio desperdicava meia tela.
//
// A fila vem inteira do GET /kds/pedidos: so producao e pronto, com o estado
// de cada item e o setor ja resolvido pelo servidor. Antes a tela baixava os
// 500 pedidos mais recentes a cada 3 s e jogava fora 95% no navegador.
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import BarraDesfazer from "./components/BarraDesfazer";
import ColunaKds from "./components/ColunaKds";
import KdsHeader from "./components/KdsHeader";
import ModoGarcom from "./components/ModoGarcom";
import useDesfazer, { SEGUNDOS_DESFAZER } from "./hooks/useDesfazer";
import useGarcom from "./hooks/useGarcom";
import useNumpad from "./hooks/useNumpad";
import useSelecaoItens from "./hooks/useSelecaoItens";
import { acaoChamarEntregador, acaoStatus } from "./lib/acoes";
import { avisarEntregadorChamado, avisarFalhaUp } from "./lib/avisosUp";
import { montarColunas } from "./lib/colunas";
import { chaveDaComanda, separarEnvios } from "./lib/envios";
import { acaoDoCard, API, COLUMNS, getAuthHeader, LIMIARES_PADRAO } from "./lib/kds";
import { montarGarcom, pratosParaLevar } from "./lib/garcom";
import { guardarModo, guardarSetor, guardarVistaGarcom, modoInicial, setorInicial, setorValido, vistaGarcomInicial } from "./lib/telaKds";
import { useSaidaQrLigada } from "../mesa-qr/useMesaQrLigado";

const LG_PX = 1024;
function useEhDesktop() {
  const calc = () => typeof window !== "undefined" && window.innerWidth >= LG_PX;
  const [v, setV] = useState(calc);
  useEffect(() => {
    const f = () => setV(calc());
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  return v;
}

const BIP = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdGaJjImAcWFkbHyKj4yDdWdlaHqGioyGfXNqaXd/hIWEg4KDhIaHh4WDgYCBg4WGhYOBgH+BhIaFg4F/f4GEhoWDgX9/gYSGhYOBf3+BhIaFg4F/f4GEhoWDgYB/gYSGhYOBf3+Bg4WFg4KAf4GDhYaDgoF/gIOFhYOCgX+Ag4WFg4KBf4CDhYWDgoF/gIOFhYOCgX+Ag4WFhIKBf4CDhYWEgoGA";

const SEM_SELECAO = { card: -1 };

export default function KdsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const desktop = useEhDesktop();
  const [pedidos, setPedidos] = useState([]);
  const [products, setProducts] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("todos");        // desktop
  const [mobileTab, setMobileTab] = useState("producao"); // celular
  // O setor e' desta TELA, nao da loja: o monitor do bar abre no bar, o da
  // cozinha na cozinha, mesmo todos entrando com o login da loja.
  const [setorFilter, setSetorFilter] = useState(() => setorInicial(searchParams));
  // Cozinha ou Garçom: o papel DESTE aparelho, guardado nele como o setor.
  const [modo, setModo] = useState(() => modoInicial(searchParams));
  const [vistaGarcom, setVistaGarcom] = useState(() => vistaGarcomInicial(searchParams));
  const modoRef = useRef(modo);
  modoRef.current = modo;
  const [selecao, setSelecao] = useState(SEM_SELECAO);
  const [pulseProducao, setPulseProducao] = useState(false);
  const [limiares, setLimiares] = useState(LIMIARES_PADRAO);
  // Desfazer a última ação: chave da loja em Preferências (ligada por padrão)
  const [desfazerLigado, setDesfazerLigado] = useState(true);
  const [upAtivo, setUpAtivo] = useState(false);   // integracao UP Entrega ligada?
  const [upStatus, setUpStatus] = useState({});    // pedido_id -> status da corrida
  const prevCountRef = useRef(0);
  const prevProducaoRef = useRef(0);
  const numpadBufferRef = useRef("");
  const numpadTimerRef = useRef(null);

  const fetchPedidos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kds/pedidos`, getAuthHeader());
      const lista = (data?.pedidos || []).map((p) => ({ ...p, items: Array.isArray(p.items) ? p.items : [] }));
      // Conta COMANDAS, nao pedidos: o envio novo da mesa entra no mesmo
      // pedido e tem de bipar igual a um pedido novo.
      const comandas = separarEnvios(lista);
      // O bipe e' da cozinha (entrou comanda). A tela do garcom tem o som dela
      // (prato pronto), senao os dois tocariam juntos e ninguem saberia qual.
      if (modoRef.current === "cozinha" && prevCountRef.current > 0 && comandas.length > prevCountRef.current) {
        try { const a = new Audio(BIP); a.volume = 0.3; a.play().catch(() => {}); } catch (_) { /* sem audio */ }
      }
      // Pedido novo em producao enquanto o celular esta na aba Prontos: o
      // contador da aba salta por alguns segundos pra chamar o olho.
      const emProducao = comandas.filter((p) => p.status === "producao").length;
      if (emProducao > prevProducaoRef.current && prevCountRef.current > 0) {
        setPulseProducao(true);
        setTimeout(() => setPulseProducao(false), 4000);
      }
      prevProducaoRef.current = emProducao;
      prevCountRef.current = comandas.length;
      setPedidos(lista);
      // Setor criado agora aparece sozinho: vem na mesma resposta, entao a
      // tela da cozinha nao precisa mais ser recarregada pra enxergar.
      const listaSetores = data?.setores || [];
      setSetores(listaSetores);
      // Setor apagado no cadastro nao pode deixar esta tela presa numa fila
      // vazia pra sempre.
      setSetorFilter((atual) => setorValido(atual, listaSetores));
    } catch (err) {
      console.error("[KDS] Erro ao buscar pedidos:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fotos dos itens: o pedido nem sempre guarda a foto, entao o cadastro de
  // produtos e' o resgate. Lido uma vez — foto nao muda no meio do turno.
  useEffect(() => {
    axios.get(`${API}/products`, getAuthHeader())
      .then((r) => setProducts(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  // Limiares de cor e a chave do Desfazer (Configuracoes > Sistema >
  // Preferencias). Reconsulta a cada minuto: a tela da cozinha fica aberta o
  // dia inteiro.
  useEffect(() => {
    const ler = () => axios.get(`${API}/settings/kds`, getAuthHeader())
      .then((r) => {
        const d = r.data || {};
        if (d.limiar_amarelo && d.limiar_vermelho) setLimiares({ amarelo: Number(d.limiar_amarelo), vermelho: Number(d.limiar_vermelho) });
        setDesfazerLigado(d.desfazer !== false);
      })
      .catch(() => {});
    ler();
    const id = setInterval(ler, 60000);
    return () => clearInterval(id);
  }, []);

  // UP Entrega: a coluna Prontos so oferece "Chamar entregador" com a
  // integracao ligada. Mesma leitura do Delivery (/entregaai/config.enabled).
  useEffect(() => {
    const ler = () => axios.get(`${API}/entregaai/config`, getAuthHeader())
      .then((r) => setUpAtivo(!!r.data?.enabled))
      .catch(() => setUpAtivo(false));
    ler();
    const id = setInterval(ler, 60000);
    return () => clearInterval(id);
  }, []);

  const lerStatusUp = useCallback(async (lista) => {
    const ids = (lista || []).filter((p) => p.status === "pronto" && (p.tipo_entrega || "").toLowerCase() === "delivery").map((p) => p.id);
    if (!ids.length) { setUpStatus({}); return; }
    try {
      const r = await axios.get(`${API}/entregaai/orders-status?pedido_ids=${ids.join(",")}`, getAuthHeader());
      setUpStatus(r.data || {});
    } catch (_) { /* mantem o ultimo */ }
  }, []);

  // Status das corridas em andamento (15 s, como no Delivery — a API da UP
  // tem latencia e cada tick vira N chamadas la dentro).
  useEffect(() => {
    if (!upAtivo) { setUpStatus({}); return undefined; }
    lerStatusUp(pedidos);
    const id = setInterval(() => lerStatusUp(pedidos), 15000);
    return () => clearInterval(id);
  }, [upAtivo, pedidos, lerStatusUp]);

  useEffect(() => {
    fetchPedidos();
    const id = setInterval(fetchPedidos, 3000);
    const handler = () => fetchPedidos();
    window.addEventListener("pedidosUpdated", handler);
    return () => { clearInterval(id); window.removeEventListener("pedidosUpdated", handler); };
  }, [fetchPedidos]);

  // Nenhuma ação sai direto pro servidor: ela aparece nesta tela na hora e só
  // vale quando o tempo do Desfazer acaba (ou na ação seguinte). `pedidos` é o
  // que o servidor mandou; `vistos`, o que esta tela mostra.
  const acoes = useDesfazer({ ativo: desfazerLigado, recarregar: fetchPedidos });
  // No celular do garçom o canto de baixo é do botão "Ler QR de saída": a barra sobe.
  const temBotaoDeSaida = useSaidaQrLigada();
  const pedidosRef = useRef(pedidos);
  pedidosRef.current = pedidos;
  const vistos = acoes.aplicar(pedidos);

  const avancarStatus = (card, status) => acoes.agendar(acaoStatus(card, status));
  const chamarEntregador = (card) => acoes.agendar(acaoChamarEntregador(card, {
    aoGravar: avisarEntregadorChamado, aoFalhar: avisarFalhaUp, depois: () => lerStatusUp(pedidosRef.current),
  }));

  // Um cartão por envio do garçom (lib/envios.js).
  const fila = separarEnvios(vistos);
  // O som do garçom ouve a fila do servidor: desfazer uma entrega não é prato novo.
  const garcom = useGarcom({ ativo: modo === "garcom", pedidos: separarEnvios(pedidos), agendar: acoes.agendar });
  // Tocar no produto só seleciona; o botão da comanda confirma (regra do dono).
  const itens = useSelecaoItens({ pedidos: fila, agendar: acoes.agendar });

  /** Um unico ponto de acao — botao do card e Enter do numpad passam aqui. */
  const executarAcao = async (card, acao) => {
    const col = card._col || COLUMNS.find((c) => c.status === card.status);
    const selecionados = card._parcial ? 0 : itens.selecionadosDe(chaveDaComanda(card)).length;
    const a = acao || acaoDoCard(card, col, { ativo: upAtivo, status: upStatus[card.id] }, selecionados);
    if (a.tipo === "marcar_selecionados") return itens.marcarSelecionados(card);
    if (a.tipo === "confirmar_cancelamento") return itens.confirmarCancelamento(card);
    // Tela de um setor: marca só o que o cartão mostra — o status do pedido
    // subiria junto os produtos das outras praças (dono, 25/09/2026).
    if (a.tipo === "avancar" && col.status === "producao" && setorFilter !== "todos") return itens.marcarDoCartao(card);
    // Envio da mesa: sobe só ele — o status do pedido subiria os outros juntos.
    if (a.tipo === "avancar" && card._envios > 1 && col.status === "producao") return itens.marcarEnvio(card);
    if (a.tipo === "avancar") return avancarStatus(card, col.nextStatus);
    if (a.tipo === "chamar") return chamarEntregador(card);
    if (a.tipo === "entregar") return garcom.entregarTudo(card, card._itens || [], { daCozinha: true });
    return undefined; // retirada / sem integracao / corrida andando / cartao parcial
  };

  const escolherVistaGarcom = (v) => {
    setVistaGarcom(v);
    guardarVistaGarcom(v);
  };

  const escolherModo = (m) => {
    setModo(m);
    guardarModo(m);
    setSelecao(SEM_SELECAO);
    itens.limparTudo();
  };

  /** Escolher o setor fixa a escolha NESTE aparelho, nao na conta. */
  const escolherSetor = (setor) => {
    setSetorFilter(setor);
    guardarSetor(setor);
    // A fila muda debaixo da selecao: segurar faria o botao marcar produto que
    // a tela nao mostra mais.
    setSelecao(SEM_SELECAO);
    itens.limparTudo();
  };

  const { producao, prontos } = montarColunas(fila, { setorFilter, setores });
  const listasGarcom = montarGarcom(fila);
  const contagemGarcom = { paraLevar: pratosParaLevar(listasGarcom.paraLevar), entregues: listasGarcom.entregues.length };
  const listaDaColuna = (status) => (status === "producao" ? producao : prontos);

  const counts = { todos: producao.length + prontos.length, producao: producao.length, pronto: prontos.length };
  const visibleColumns = desktop
    ? (filter === "todos" ? COLUMNS : COLUMNS.filter((c) => c.status === filter))
    : COLUMNS.filter((c) => c.status === mobileTab);
  const cardsVisiveis = visibleColumns.flatMap((col) => listaDaColuna(col.status).map((p) => ({ ...p, _col: col })));

  // Enter do numpad: com produto escolhido marca so ele, sem produto avanca
  // a comanda inteira.
  const confirmarNumpad = (card) => executarAcao(card);
  useNumpad({
    ativo: desktop && modo === "cozinha", cards: cardsVisiveis, selecao, setSelecao,
    onConfirmar: confirmarNumpad, onAlternar: itens.alternar,
    bufferRef: numpadBufferRef, timerRef: numpadTimerRef,
  });

  const cardSelecionado = selecao.card >= 0 && selecao.card < cardsVisiveis.length ? cardsVisiveis[selecao.card] : null;

  return (
    <div className="w-screen flex flex-col bg-muted/20 overflow-hidden" style={{ height: "100dvh" }}>
      <style>{`
        @keyframes kds-selected-pulse { 0%,100% { box-shadow: 0 0 0 4px rgba(249,115,22,.4), 0 8px 25px rgba(249,115,22,.25);} 50% { box-shadow: 0 0 0 6px rgba(249,115,22,.2), 0 8px 30px rgba(249,115,22,.15);} }
        /* cada card com a propria altura; quem rola e' a coluna */
        .kds-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: .75rem; align-items: start; }
        @media (min-width: 1536px) { .kds-grid { grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); } }
      `}</style>

      <KdsHeader
        desktop={desktop} filter={filter} onFilter={setFilter} mobileTab={mobileTab} onMobileTab={setMobileTab}
        counts={counts} pulseProducao={pulseProducao} setores={setores} setorFilter={setorFilter} onSetor={escolherSetor}
        onRefresh={fetchPedidos} loading={loading} onBack={() => navigate("/admin/")}
        modo={modo} onModo={escolherModo}
        vistaGarcom={vistaGarcom} onVistaGarcom={escolherVistaGarcom} contagemGarcom={contagemGarcom}
      />

      {desktop && modo === "cozinha" && cardSelecionado && (
        <div className="flex items-center justify-center gap-2 px-3 py-1.5 bg-orange-500 text-white text-xs font-medium">
          <span>Comanda {cardSelecionado.codigo} ({selecao.card + 1})</span>
          {itens.selecionadosDe(chaveDaComanda(cardSelecionado)).length > 0
            ? <span className="opacity-90">{itens.selecionadosDe(chaveDaComanda(cardSelecionado)).length} selecionado(s) · ENTER marca pronto | número de novo tira | ESC solta</span>
            : <span className="opacity-70">número do produto seleciona · ENTER sem seleção = comanda toda | ESC solta</span>}
        </div>
      )}

      {modo === "garcom" ? (
        <ModoGarcom pedidos={fila} desktop={desktop} products={products} vista={vistaGarcom}
          onEntregar={garcom.entregar} onEntregarTudo={garcom.entregarTudo} folga={!!acoes.pendente} carregando={loading} />
      ) : (
      <div className={`flex-1 min-h-0 p-2 sm:p-3 lg:p-4 flex ${desktop ? "flex-row gap-4" : "flex-col"}`}>
        {visibleColumns.map((col) => (
          <ColunaKds key={col.status} col={col} lista={listaDaColuna(col.status)} desktop={desktop} products={products}
            indexOf={(p) => cardsVisiveis.findIndex((v) => v._cardId === p._cardId)} selecao={selecao}
            onAcao={executarAcao} itens={itens} limiares={limiares} up={{ ativo: upAtivo, status: upStatus }}
            folga={!!acoes.pendente} carregando={loading} />
        ))}
      </div>
      )}

      {acoes.pendente && (
        <BarraDesfazer key={acoes.pendente.id} acao={acoes.pendente} segundos={SEGUNDOS_DESFAZER} onDesfazer={acoes.desfazer}
          mobile={!desktop} alta={!desktop && modo === "garcom" && temBotaoDeSaida} comTecla={desktop} />
      )}
    </div>
  );
}
